import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  XMarkIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  BoltIcon,
  CalendarDaysIcon,
  CircleStackIcon,
  CreditCardIcon,
  Squares2X2Icon,
  QuestionMarkCircleIcon,
  ArrowTopRightOnSquareIcon,
  BuildingOfficeIcon,
  BuildingOffice2Icon,
  GlobeAltIcon,
  PaintBrushIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

import { useFullLogo } from '../../hooks/useFullLogo';
import { TbPlug } from 'react-icons/tb';
import nvgtrLogoBlack from '../../assets/nvgtrLogo_Black.svg';
import nvgtrLogoWhite from '../../assets/nvgtrLogo_White.svg';

import ManageConnectorsTab from './AccountModal/ManageConnectorsTab';
import AccountTab from './AccountModal/AccountTab';
import SettingsTab from './AccountModal/SettingsTab';
import BillingTab from './AccountModal/BillingTab';
import UsageTab from './AccountModal/UsageTab';
import SecurityTab from './AccountModal/SecurityTab';
import OrganizationsManagementSection from '../../features/profile/sections/OrganizationsManagementSection';
import OrganizationSection from '../../features/profile/sections/OrganizationSection';
import DomainSection from '../../features/profile/sections/DomainSection';
import BrandSection from '../../features/profile/sections/BrandSection';
import { canAccessCustomDomain, canAccessCustomBranding, canManageOrganization, getUserRole } from '../../utils/planPermissions';

import './AccountModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';

const SIDEBAR_ITEMS = [
  { id: 'account', label: 'Account', Icon: UserCircleIcon },
  { id: 'settings', label: 'Settings', Icon: Cog6ToothIcon },
  { id: 'security', label: 'Security', Icon: ShieldCheckIcon },
  { id: 'organization', label: 'Organization', Icon: BuildingOfficeIcon },
  { id: 'billing', label: 'Billing', Icon: CreditCardIcon },
  { id: 'usage', label: 'Usage', Icon: BoltIcon },
  { id: 'domain', label: 'Domain', Icon: GlobeAltIcon },
  { id: 'brand', label: 'Brand', Icon: PaintBrushIcon },
  { id: 'scheduled', label: 'Scheduled tasks', Icon: CalendarDaysIcon },
  { id: 'data', label: 'Data controls', Icon: CircleStackIcon },
  { id: 'personalization', label: 'Personalization', Icon: Squares2X2Icon },
  { id: 'connectors', label: 'Connectors', Icon: TbPlug },
];

const SIDEBAR_PINNED_ITEMS = [
  { id: 'manageOrganizations', label: 'Manage organizations', Icon: BuildingOffice2Icon },
  { id: 'help', label: 'Get help', Icon: QuestionMarkCircleIcon, external: true, url: 'https://heymidus.com' },
];

const TAB_TITLES = {
  account: 'Account',
  organization: 'Organization',
  domain: 'Domain',
  brand: 'Brand',
  manageOrganizations: 'Manage organizations',
  settings: 'Settings',
  security: 'Security',
  usage: 'Usage',
  scheduled: 'Scheduled tasks',
  mail: 'Mail',
  data: 'Data controls',
  cloud: 'Cloud browser',
  personalization: 'Personalization',
  skills: 'Skills',
  connectors: 'My Connectors',
  integrations: 'Integrations',
  billing: 'Billing',
};

/**
 * Account modal shell with sidebar navigation. Tab content is rendered based on active tab.
 * Each tab lives in its own file under AccountModal/ (e.g. ManageConnectorsTab.js).
 * Connector data is cached at this level so it persists when switching tabs.
 */
const AccountModal = ({ isOpen, onClose, darkMode, onAddConnectors, user, setUser }) => {
  const fullLogoUrls = useFullLogo();
  const fullLogoUrl = darkMode ? fullLogoUrls.dark : fullLogoUrls.light;
  const [activeSidebarItem, setActiveSidebarItemState] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab) return tab;
      return localStorage.getItem('accountModalTab') || 'connectors';
    } catch {
      return 'connectors';
    }
  });

  const setActiveSidebarItem = (tabId) => {
    setActiveSidebarItemState(tabId);
    try {
      localStorage.setItem('accountModalTab', tabId);
      const params = new URLSearchParams(window.location.search);
      params.set('tab', tabId);
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    } catch {}
  };

  // Sync tab when modal opens (e.g. sidebar profile click opens to account/profile tab)
  useEffect(() => {
    if (isOpen) {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab') || localStorage.getItem('accountModalTab') || 'account';
      setActiveSidebarItemState(tab);
    }
  }, [isOpen]);

  // Listen for tab switch from within modal (e.g. "Set up domain" link in Organization)
  useEffect(() => {
    const handler = (e) => {
      const tab = e.detail?.tab;
      if (tab && isOpen) {
        setActiveSidebarItemState(tab);
        try {
          localStorage.setItem('accountModalTab', tab);
          const params = new URLSearchParams(window.location.search);
          params.set('tab', tab);
          window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
        } catch {}
      }
    };
    window.addEventListener('open-account-modal', handler);
    return () => window.removeEventListener('open-account-modal', handler);
  }, [isOpen]);

  const [connectedConnectors, setConnectedConnectors] = useState(null);
  const [connectorsLoading, setConnectorsLoading] = useState(false);

  const fetchConnectorStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setConnectedConnectors({});
      setConnectorsLoading(false);
      return;
    }
    try {
      setConnectorsLoading(true);
      const [driveRes, gmailRes, calendarRes, hubspotRes, githubRes, notionRes, outlookRes, outlookCalendarRes, sharepointRes, onedriveRes] = await Promise.allSettled([
        axios.get(`${API_URL}/auth/google-drive/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/google-drive/status?connector=gmail`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/google-drive/status?connector=calendar`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/hubspot/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/github/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/notion/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/outlook/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/outlook-calendar/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/sharepoint/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/auth/onedrive/status`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setConnectedConnectors({
        'google-drive': driveRes.status === 'fulfilled' && driveRes.value?.data?.connected,
        gmail: gmailRes.status === 'fulfilled' && gmailRes.value?.data?.connected,
        'google-calendar': calendarRes.status === 'fulfilled' && calendarRes.value?.data?.connected,
        hubspot: hubspotRes.status === 'fulfilled' && hubspotRes.value?.data?.connected,
        github: githubRes.status === 'fulfilled' && githubRes.value?.data?.connected,
        notion: notionRes.status === 'fulfilled' && notionRes.value?.data?.connected,
        'outlook-mail': outlookRes.status === 'fulfilled' && outlookRes.value?.data?.connected,
        'outlook-calendar': outlookCalendarRes.status === 'fulfilled' && outlookCalendarRes.value?.data?.connected,
        sharepoint: sharepointRes.status === 'fulfilled' && sharepointRes.value?.data?.connected,
        onedrive: onedriveRes.status === 'fulfilled' && onedriveRes.value?.data?.connected,
      });
    } catch (e) {
      console.error('Error fetching connector status:', e);
    } finally {
      setConnectorsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && connectedConnectors === null) {
      fetchConnectorStatus();
    } else if (!isOpen) {
      setConnectedConnectors(null);
    }
  }, [isOpen, connectedConnectors, fetchConnectorStatus]);

  if (!isOpen) return null;

  const currentTitle = TAB_TITLES[activeSidebarItem] ?? activeSidebarItem;

  const renderTabContent = () => {
    switch (activeSidebarItem) {
      case 'account':
        return user && setUser ? (
          <AccountTab
            user={user}
            setUser={setUser}
          />
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col items-center justify-center py-16 text-center">
            <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Sign in to manage your profile
            </p>
          </div>
        );
      case 'organization':
        return user ? (
          <OrganizationSection user={user} activeTab="members" />
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col items-center justify-center py-16 text-center">
            <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Sign in to manage your organization
            </p>
          </div>
        );
      case 'domain':
        return user && canAccessCustomDomain(user) && canManageOrganization(user, getUserRole()) ? (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-0 pb-4">
            <DomainSection user={user} />
          </div>
        ) : user && canAccessCustomDomain(user) ? (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col items-center justify-center py-16 text-center">
            <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Only organization administrators and owners can manage custom domains.
            </p>
          </div>
        ) : user ? (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col items-center justify-center py-16 text-center">
            <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Custom domain is available on Pro and Max plans.
            </p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col items-center justify-center py-16 text-center">
            <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Sign in to manage your domain
            </p>
          </div>
        );
      case 'brand':
        return user && canAccessCustomBranding(user) && canManageOrganization(user, getUserRole()) ? (
          <BrandSection />
        ) : user && canAccessCustomBranding(user) ? (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col items-center justify-center py-16 text-center">
            <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Only organization administrators and owners can manage brand assets.
            </p>
          </div>
        ) : user ? (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col items-center justify-center py-16 text-center">
            <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Custom branding is available on Pro and Max plans.
            </p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col items-center justify-center py-16 text-center">
            <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Sign in to manage your brand
            </p>
          </div>
        );
      case 'manageOrganizations':
        return user && setUser ? (
          <OrganizationsManagementSection user={user} setUser={setUser} />
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col items-center justify-center py-16 text-center">
            <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Sign in to manage your organizations
            </p>
          </div>
        );
      case 'settings':
        return <SettingsTab onClose={onClose} user={user} setUser={setUser} />;
      case 'security':
        return <SecurityTab />;
      case 'billing':
        return <BillingTab />;
      case 'usage':
        return <UsageTab />;
      case 'connectors':
        return (
          <ManageConnectorsTab
            onClose={onClose}
            onAddConnectors={onAddConnectors}
            connectedConnectors={connectedConnectors}
            connectorsLoading={connectorsLoading}
            onRefreshConnectors={fetchConnectorStatus}
            darkMode={darkMode}
          />
        );
      default:
        return (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col items-center justify-center py-16 text-center">
            <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Coming soon
            </p>
          </div>
        );
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="account-modal relative w-full max-w-4xl h-[75vh] min-h-[480px] max-h-[90vh] flex bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-xl border border-gray-200 dark:border-[#2a2a2a] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left sidebar */}
        <div className="w-56 flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#141414]">
          <div className="p-6 flex items-center gap-2">
            <img
              src={fullLogoUrl}
              alt="Logo"
              className="h-6"
              onError={(e) => { e.target.src = darkMode ? nvgtrLogoWhite : nvgtrLogoBlack; }}
            />
          </div>
          <nav className="flex-1 flex flex-col gap-0.5 px-2 py-0.5 overflow-y-auto min-h-0">
            {SIDEBAR_ITEMS.filter((item) => {
              if (item.id === 'domain') return user && canAccessCustomDomain(user);
              if (item.id === 'brand') return user && canAccessCustomBranding(user);
              return true;
            }).map((item) => {
              const Icon = item.Icon;
              const isActive = activeSidebarItem === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveSidebarItem(item.id)}
                  className={`account-modal-item w-full flex items-center rounded-xl gap-2 px-4 py-2 text-left text-sm ${
                    isActive
                      ? 'bg-gray-200/80 dark:bg-[#252525] text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-[#999999]'
                  }`}
                  style={{ fontFamily: 'var(--chat-font-family)' }}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
          <div className="flex-shrink-0 flex flex-col gap-0.5 px-2 py-2">
            {SIDEBAR_PINNED_ITEMS.map((item) => {
              const Icon = item.Icon;
              const isActive = activeSidebarItem === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    if (item.external && item.url) {
                      window.open(item.url, '_blank', 'noopener,noreferrer');
                    } else if (!item.external) {
                      setActiveSidebarItem(item.id);
                    }
                  }}
                  className={`account-modal-item w-full flex items-center rounded-xl gap-2 px-4 py-2 text-left text-sm ${
                    isActive
                      ? 'bg-gray-200/80 dark:bg-[#252525] text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-[#999999]'
                  }`}
                  style={{ fontFamily: 'var(--chat-font-family)' }}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span>{item.label}</span>
                  {item.external && <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 ml-auto opacity-60" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right content */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[#2a2a2a] flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'var(--chat-font-family)' }}>
              {currentTitle}
            </h2>
            <button
              onClick={onClose}
              className="account-modal-btn p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg"
              aria-label="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="account-modal-content flex-1 min-h-0 flex flex-col overflow-hidden">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AccountModal;
